/**
 * The TV's identity across power-ons (TV_REVIVAL_PLAN.md §1).
 *
 * After the first pairing the server issues a long-lived device token through
 * the session document; the page stores it here and every later load calls
 * `resumeTvSession` with it instead of showing a code. Nothing in this module
 * touches the network; the decisions are pure so they can be unit-tested.
 */

export const TV_DEVICE_STORAGE_KEY = 'lagking.tv.device';

export interface StoredTvDevice {
  deviceId: string;
  token: string;
  name: string;
}

/** Parse the stored JSON. Anything malformed is treated as "no device". */
export function parseStoredDevice(raw: string | null | undefined): StoredTvDevice | null {
  if (!raw) return null;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const deviceId = typeof record.deviceId === 'string' ? record.deviceId.trim() : '';
  const token = typeof record.token === 'string' ? record.token.trim() : '';
  if (!deviceId || !token) return null;
  const name = typeof record.name === 'string' && record.name.trim() ? record.name.trim() : 'LagKing TV';
  return { deviceId, token, name };
}

export function loadStoredDevice(): StoredTvDevice | null {
  try {
    return parseStoredDevice(window.localStorage.getItem(TV_DEVICE_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function storeDevice(device: StoredTvDevice): boolean {
  try {
    window.localStorage.setItem(TV_DEVICE_STORAGE_KEY, JSON.stringify(device));
    return true;
  } catch {
    return false;
  }
}

export function clearStoredDevice(): void {
  try {
    window.localStorage.removeItem(TV_DEVICE_STORAGE_KEY);
  } catch {
    // Nothing to clear, or storage is unavailable; the next load pairs again.
  }
}

export type ResumeFailureAction = 'pair' | 'retry';

/**
 * What to do when `resumeTvSession` rejects. A revoked or unknown token means
 * the account removed this TV (or a second account took it over): forget the
 * token and show a pairing code. Anything else (network, quota, a cold
 * function) is transient: keep the token and try again with backoff.
 *
 * `code` is the Firebase callable error code, with or without the
 * `functions/` prefix.
 */
export function resumeFailureAction(code: string | null | undefined): ResumeFailureAction {
  const normalized = String(code ?? '').toLowerCase().replace(/^functions\//, '');
  switch (normalized) {
    case 'not-found':
    case 'permission-denied':
    case 'unauthenticated':
    case 'invalid-argument':
      return 'pair';
    default:
      return 'retry';
  }
}

/** Reconnect backoff: 5 s, 15 s, then 60 s for every later attempt. */
export const RESUME_BACKOFF_MS = [5_000, 15_000, 60_000] as const;

export function resumeBackoffMs(attempt: number): number {
  const index = Math.max(0, Math.min(RESUME_BACKOFF_MS.length - 1, Math.floor(attempt)));
  return RESUME_BACKOFF_MS[index];
}

/**
 * Whether a session document carries a token this TV should store. Returns
 * the device to store, or null when nothing new is on the document.
 */
export function deviceFromIssue(
  issue: { token?: unknown; deviceId?: unknown } | null | undefined,
  current: StoredTvDevice | null,
  name: string,
): StoredTvDevice | null {
  if (!issue) return null;
  const token = typeof issue.token === 'string' ? issue.token.trim() : '';
  const deviceId = typeof issue.deviceId === 'string' ? issue.deviceId.trim() : '';
  if (!token || !deviceId) return null;
  if (current && current.token === token && current.deviceId === deviceId) return null;
  return { deviceId, token, name: current?.name ?? name };
}
